import {
  FranchiseSnapshotModel,
  FranchiseEditionLinkedEntityModel,
  AggregatedMetrics,
  EntityWithComparison,
  ProcessedEdition,
  GroupedByYear
} from './types';

/**
 * Get a representative currency code for the franchise (the first linked
 * entity's currency). Mixed-currency franchises are surfaced separately via
 * hasMixedCurrencies / CurrencyWarning.
 */
export const getFranchiseCurrency = (
  franchise: FranchiseSnapshotModel
): string | undefined => {
  for (const edition of franchise.editions || []) {
    for (const entity of edition.linkedEntities || []) {
      if (entity.currency) {
        return entity.currency;
      }
    }
  }
  return undefined;
};

/**
 * Return the currency shared by every entity in a group, or undefined when the
 * group is empty or spans more than one currency. Callers use this to avoid
 * rendering a summed total under an arbitrary currency symbol.
 */
export const getSharedCurrency = (
  entities: Array<{ currency?: string }>
): string | undefined => {
  const currencies = new Set<string>();
  for (const entity of entities) {
    if (entity.currency) {
      currencies.add(entity.currency);
    }
  }
  return currencies.size === 1 ? currencies.values().next().value : undefined;
};

/**
 * Get the display year for an entity (entity.year || edition.year)
 */
export const getEntityYear = (
  entity: FranchiseEditionLinkedEntityModel,
  editionYear: number | null
): number | null => {
  return entity.year !== null ? entity.year : editionYear;
};

/**
 * Check if a franchise has mixed currencies
 */
export const hasMixedCurrencies = (franchise: FranchiseSnapshotModel): boolean => {
  const currencies = new Set<string>();
  
  franchise.editions.forEach(edition => {
    edition.linkedEntities.forEach(entity => {
      if (entity.currency) {
        currencies.add(entity.currency);
      }
    });
  });
  
  return currencies.size > 1;
};

/**
 * Get all unique currencies in the franchise
 */
export const getUniqueCurrencies = (franchise: FranchiseSnapshotModel): string[] => {
  const currencies = new Set<string>();
  
  franchise.editions.forEach(edition => {
    edition.linkedEntities.forEach(entity => {
      if (entity.currency) {
        currencies.add(entity.currency);
      }
    });
  });
  
  return Array.from(currencies);
};

/**
 * Map metric field names based on "Include Offline" toggle
 */
export const getMetricFieldName = (
  baseField: string,
  includeOffline: boolean
): string => {
  // Fields with online/offline variants
  const mappings: { [key: string]: { offline: string; online: string } } = {
    donationCount: { offline: 'donationCount', online: 'donationOnlineCount' },
    donationRaisedAmount: { offline: 'donationRaisedAmount', online: 'donationRaisedOnlineAmount' },
    saleCount: { offline: 'saleCount', online: 'saleOnlineCount' },
    saleRaisedAmount: { offline: 'saleRaisedAmount', online: 'saleRaisedOnlineAmount' },
    donationAverageAmount: { offline: 'donationAverageAmount', online: 'donationAverageOnlineAmount' },
  };

  if (mappings[baseField]) {
    return includeOffline ? mappings[baseField].offline : mappings[baseField].online;
  }

  // No variant, return as-is
  return baseField;
};

/**
 * Get the value of a metric from an entity, respecting the offline toggle
 */
export const getMetricValue = (
  entity: FranchiseEditionLinkedEntityModel,
  baseField: string,
  includeOffline: boolean
): number => {
  // Special handling for raisedAmount - calculate online version
  if (baseField === 'raisedAmount' && !includeOffline) {
    const saleOnline = entity.saleRaisedOnlineAmount || 0;
    const donationOnline = entity.donationRaisedOnlineAmount || 0;
    return saleOnline + donationOnline;
  }
  
  const fieldName = getMetricFieldName(baseField, includeOffline);
  const value = (entity as any)[fieldName];
  return value !== null && value !== undefined ? Number(value) : 0;
};

/**
 * Aggregate metrics across all entities in the franchise
 */
export const aggregateFranchiseMetrics = (
  franchise: FranchiseSnapshotModel,
  includeOffline: boolean
): AggregatedMetrics => {
  const allEntities: FranchiseEditionLinkedEntityModel[] = [];
  franchise.editions.forEach(edition => {
    allEntities.push(...edition.linkedEntities);
  });

  if (allEntities.length === 0) {
    return createEmptyMetrics();
  }

  // Sum metrics
  let donationCount = 0;
  let donationOnlineCount = 0;
  let saleCount = 0;
  let saleOnlineCount = 0;
  let pledgeCount = 0;
  let saleDetailCount = 0;

  let donationRaisedAmount = 0;
  let donationRaisedOnlineAmount = 0;
  let saleRaisedAmount = 0;
  let saleRaisedOnlineAmount = 0;
  let pledgeRaisedAmount = 0;
  let fundraiserRaisedAmount = 0;
  let raisedAmount = 0;

  let feeCollected = 0;
  let donationFeeCollected = 0;
  let saleFeeCollected = 0;

  // For averaging percentages
  let feeCoverageSum = 0;
  let feeCoverageCount = 0;
  let feeAmountCoverageSum = 0;
  let feeAmountCoverageCount = 0;

  // Calculate page counts
  let fundraiserCount = 0;
  let fundraiserWithDonationsCount = 0;
  let teamCount = 0;
  let teamWithDonationsCount = 0;
  let collectionCount = 0;
  let collectionWithDonationsCount = 0;

  // Calculate ambassador metrics
  let ambassadorTotalCount = 0;
  let ambassadorPageCount = 0;
  let ambassadorFundraisedAmount = 0;
  let ambassadorFundraisedOnlineAmount = 0;

  allEntities.forEach(entity => {
    // Counts
    donationCount += entity.donationCount || 0;
    donationOnlineCount += entity.donationOnlineCount || 0;
    saleCount += entity.saleCount || 0;
    saleOnlineCount += entity.saleOnlineCount || 0;
    pledgeCount += entity.pledgeCount || 0;
    saleDetailCount += entity.saleDetailCount || 0;

    // Amounts
    donationRaisedAmount += entity.donationRaisedAmount || 0;
    donationRaisedOnlineAmount += entity.donationRaisedOnlineAmount || 0;
    saleRaisedAmount += entity.saleRaisedAmount || 0;
    saleRaisedOnlineAmount += entity.saleRaisedOnlineAmount || 0;
    pledgeRaisedAmount += entity.pledgeRaisedAmount || 0;
    fundraiserRaisedAmount += entity.fundraiserRaisedAmount || 0;
    raisedAmount += entity.raisedAmount || 0;

    // Fees
    feeCollected += entity.feeCollected || 0;
    donationFeeCollected += entity.donationFeeCollected || 0;
    saleFeeCollected += entity.saleFeeCollected || 0;

    // Fee percentages (for averaging)
    if (entity.feeCoveragePercentage !== null && entity.feeCoveragePercentage !== undefined) {
      feeCoverageSum += entity.feeCoveragePercentage;
      feeCoverageCount++;
    }
    if (entity.feeAmountCoveragePercentage !== null && entity.feeAmountCoveragePercentage !== undefined) {
      feeAmountCoverageSum += entity.feeAmountCoveragePercentage;
      feeAmountCoverageCount++;
    }

    fundraiserCount += entity.fundraiserCount || 0;
    fundraiserWithDonationsCount += entity.fundraiserWithDonationsCount || 0;
    teamCount += entity.teamCount || 0;
    teamWithDonationsCount += entity.teamWithDonationsCount || 0;
    collectionCount += entity.collectionCount || 0;
    collectionWithDonationsCount += entity.collectionWithDonationsCount || 0;
    ambassadorTotalCount += entity.ambassadorTotalCount || 0;
    ambassadorPageCount += entity.ambassadorPageCount || 0;
    ambassadorFundraisedAmount += entity.ambassadorFundraisedAmount || 0;
    ambassadorFundraisedOnlineAmount += entity.ambassadorFundraisedOnlineAmount || 0;
  });
  
  const pageCount = fundraiserCount + teamCount + collectionCount;
  const pagesWithDonations = fundraiserWithDonationsCount + teamWithDonationsCount + collectionWithDonationsCount;

  // Calculate averages
  const donationAverageAmount = donationCount > 0 ? donationRaisedAmount / donationCount : 0;
  const donationAverageOnlineAmount = donationOnlineCount > 0 ? donationRaisedOnlineAmount / donationOnlineCount : 0;
  const fundraiserAverageRaisedAmount = fundraiserCount > 0 ? fundraiserRaisedAmount / fundraiserCount : 0;

  const feeCoveragePercentage = feeCoverageCount > 0 ? feeCoverageSum / feeCoverageCount : 0;
  const feeAmountCoveragePercentage = feeAmountCoverageCount > 0 ? feeAmountCoverageSum / feeAmountCoverageCount : 0;
  const feeCollectedPercentage = raisedAmount > 0 ? (feeCollected / raisedAmount) * 100 : 0;

  return {
    donationCount,
    donationOnlineCount,
    saleCount,
    saleOnlineCount,
    pledgeCount,
    saleDetailCount,
    donationRaisedAmount,
    donationRaisedOnlineAmount,
    saleRaisedAmount,
    saleRaisedOnlineAmount,
    pledgeRaisedAmount,
    fundraiserRaisedAmount,
    raisedAmount,
    donationAverageAmount,
    donationAverageOnlineAmount,
    fundraiserAverageRaisedAmount,
    feeCollected,
    donationFeeCollected,
    saleFeeCollected,
    feeCoveragePercentage,
    feeAmountCoveragePercentage,
    feeCollectedPercentage,
    fundraiserCount,
    fundraiserWithDonationsCount,
    teamCount,
    teamWithDonationsCount,
    collectionCount,
    collectionWithDonationsCount,
    pageCount,
    pagesWithDonations,
    ambassadorTotalCount,
    ambassadorPageCount,
    ambassadorFundraisedAmount,
    ambassadorFundraisedOnlineAmount
  };
};

/**
 * Create empty metrics object
 */
const createEmptyMetrics = (): AggregatedMetrics => ({
  donationCount: 0,
  donationOnlineCount: 0,
  saleCount: 0,
  saleOnlineCount: 0,
  pledgeCount: 0,
  saleDetailCount: 0,
  donationRaisedAmount: 0,
  donationRaisedOnlineAmount: 0,
  saleRaisedAmount: 0,
  saleRaisedOnlineAmount: 0,
  pledgeRaisedAmount: 0,
  fundraiserRaisedAmount: 0,
  raisedAmount: 0,
  donationAverageAmount: 0,
  donationAverageOnlineAmount: 0,
  fundraiserAverageRaisedAmount: 0,
  feeCollected: 0,
  donationFeeCollected: 0,
  saleFeeCollected: 0,
  feeCoveragePercentage: 0,
  feeAmountCoveragePercentage: 0,
  feeCollectedPercentage: 0,
  fundraiserCount: 0,
  fundraiserWithDonationsCount: 0,
  teamCount: 0,
  teamWithDonationsCount: 0,
  collectionCount: 0,
  collectionWithDonationsCount: 0,
  pageCount: 0,
  pagesWithDonations: 0,
  ambassadorTotalCount: 0,
  ambassadorPageCount: 0,
  ambassadorFundraisedAmount: 0,
  ambassadorFundraisedOnlineAmount: 0
});

/**
 * Calculate year-over-year percentage change
 */
export const calculateYoYChange = (current: number, previous: number): number | undefined => {
  if (previous === 0) {
    return current > 0 ? 100 : undefined;
  }
  return ((current - previous) / previous) * 100;
};

/**
 * Add year-over-year comparison data to entities within an edition
 */
export const addYoYComparisons = (
  edition: ProcessedEdition,
  allEntitiesSorted: EntityWithComparison[]
): EntityWithComparison[] => {
  return edition.entities.map(entity => {
    const entityYear = getEntityYear(entity, edition.edition.year);
    if (!entityYear) return entity;

    // Find previous year entity of the same type within the same edition
    const previousYearEntity = allEntitiesSorted.find(
      e =>
        e.linkedEntityType === entity.linkedEntityType &&
        getEntityYear(e, edition.edition.year) === entityYear - 1
    );

    if (!previousYearEntity) return entity;

    // Calculate YoY changes for all 12 selectable metrics
    const yoyChanges = {
      raisedAmount: calculateYoYChange(entity.raisedAmount || 0, previousYearEntity.raisedAmount || 0),
      donationCount: calculateYoYChange(entity.donationCount || 0, previousYearEntity.donationCount || 0),
      feeCollected: calculateYoYChange(entity.feeCollected || 0, previousYearEntity.feeCollected || 0),
      donationAverageAmount: calculateYoYChange(
        entity.donationAverageAmount || 0,
        previousYearEntity.donationAverageAmount || 0
      ),
      saleRaisedAmount: calculateYoYChange(entity.saleRaisedAmount || 0, previousYearEntity.saleRaisedAmount || 0),
      donationRaisedAmount: calculateYoYChange(entity.donationRaisedAmount || 0, previousYearEntity.donationRaisedAmount || 0),
      saleDetailCount: calculateYoYChange(entity.saleDetailCount || 0, previousYearEntity.saleDetailCount || 0),
      pageCount: calculateYoYChange(
        (entity.fundraiserCount || 0) + (entity.teamCount || 0) + (entity.collectionCount || 0),
        (previousYearEntity.fundraiserCount || 0) + (previousYearEntity.teamCount || 0) + (previousYearEntity.collectionCount || 0)
      ),
      pagesWithDonations: calculateYoYChange(
        (entity.fundraiserWithDonationsCount || 0) + (entity.teamWithDonationsCount || 0) + (entity.collectionWithDonationsCount || 0),
        (previousYearEntity.fundraiserWithDonationsCount || 0) + (previousYearEntity.teamWithDonationsCount || 0) + (previousYearEntity.collectionWithDonationsCount || 0)
      ),
      fundraiserAverageRaisedAmount: calculateYoYChange(
        entity.fundraiserAverageRaisedAmount || 0,
        previousYearEntity.fundraiserAverageRaisedAmount || 0
      ),
      ambassadorTotalCount: calculateYoYChange(entity.ambassadorTotalCount || 0, previousYearEntity.ambassadorTotalCount || 0),
      ambassadorPageCount: calculateYoYChange(entity.ambassadorPageCount || 0, previousYearEntity.ambassadorPageCount || 0),
      ambassadorFundraisedAmount: calculateYoYChange(
        entity.ambassadorFundraisedAmount || 0,
        previousYearEntity.ambassadorFundraisedAmount || 0
      )
    };

    return {
      ...entity,
      previousYearEntity,
      yoyChanges
    };
  });
};

/**
 * Sort entities by raised amount (descending)
 */
export const sortEntitiesByRaised = (
  entities: EntityWithComparison[]
): EntityWithComparison[] => {
  return [...entities].sort((a, b) => {
    return (b.raisedAmount || 0) - (a.raisedAmount || 0);
  });
};

/**
 * Process editions for "Named" view
 */
export const processEditionsForNamedView = (
  franchise: FranchiseSnapshotModel
): ProcessedEdition[] => {
  // Sort editions by year (descending - most recent first), then by name
  const sortedEditions = [...franchise.editions].sort((a, b) => {
    const yearA = a.year || 0;
    const yearB = b.year || 0;
    
    // First sort by year descending
    if (yearB !== yearA) {
      return yearB - yearA;
    }
    
    // If same year, sort by name
    return a.name.localeCompare(b.name);
  });

  return sortedEditions.map(edition => {
    // Sort entities by year within edition
    const entitiesSortedByYear = [...edition.linkedEntities].sort((a, b) => {
      const yearA = getEntityYear(a, edition.year) || 0;
      const yearB = getEntityYear(b, edition.year) || 0;
      return yearB - yearA; // desc
    });

    // Add YoY comparisons
    const entitiesWithComparisons = addYoYComparisons(
      {
        edition,
        entities: entitiesSortedByYear,
        displayYear: edition.year,
        totalRaised: 0 // Temporary, will calculate below
      },
      entitiesSortedByYear
    );

    // Calculate total raised for this edition
    const totalRaised = entitiesWithComparisons.reduce(
      (sum, entity) => sum + (entity.raisedAmount || 0),
      0
    );

    // Keep entities sorted by year (already sorted above)
    return {
      edition,
      entities: entitiesWithComparisons,
      displayYear: edition.year,
      totalRaised
    };
  });
};

/**
 * Process editions for "Year" view (group by year across all editions)
 */
export const processEditionsForYearView = (
  franchise: FranchiseSnapshotModel
): GroupedByYear => {
  const grouped: GroupedByYear = {};

  franchise.editions.forEach(edition => {
    edition.linkedEntities.forEach(entity => {
      const year = getEntityYear(entity, edition.year);
      if (year === null) {
        // Handle "No Year" group
        if (!grouped[0]) {
          grouped[0] = [];
        }
        grouped[0].push(entity);
      } else {
        if (!grouped[year]) {
          grouped[year] = [];
        }
        grouped[year].push(entity);
      }
    });
  });

  // Sort entities within each year and add YoY comparisons
  const processedGroups: GroupedByYear = {};
  Object.keys(grouped).forEach(yearKey => {
    const year = Number(yearKey);
    const entities = grouped[year];

    // Add YoY for year view (compare across all entities in previous year)
    const entitiesWithYoY = entities.map(entity => {
      if (year === 0) return entity; // No year, no comparison

      const previousYearEntities = grouped[year - 1] || [];
      const previousYearEntity = previousYearEntities.find(
        e => e.linkedEntityType === entity.linkedEntityType
      );

      if (!previousYearEntity) return entity;

      // Calculate YoY changes for all 12 selectable metrics
      const yoyChanges = {
        raisedAmount: calculateYoYChange(entity.raisedAmount || 0, previousYearEntity.raisedAmount || 0),
        donationCount: calculateYoYChange(entity.donationCount || 0, previousYearEntity.donationCount || 0),
        feeCollected: calculateYoYChange(entity.feeCollected || 0, previousYearEntity.feeCollected || 0),
        donationAverageAmount: calculateYoYChange(
          entity.donationAverageAmount || 0,
          previousYearEntity.donationAverageAmount || 0
        ),
        saleRaisedAmount: calculateYoYChange(entity.saleRaisedAmount || 0, previousYearEntity.saleRaisedAmount || 0),
        donationRaisedAmount: calculateYoYChange(entity.donationRaisedAmount || 0, previousYearEntity.donationRaisedAmount || 0),
        saleDetailCount: calculateYoYChange(entity.saleDetailCount || 0, previousYearEntity.saleDetailCount || 0),
        pageCount: calculateYoYChange(
          (entity.fundraiserCount || 0) + (entity.teamCount || 0) + (entity.collectionCount || 0),
          (previousYearEntity.fundraiserCount || 0) + (previousYearEntity.teamCount || 0) + (previousYearEntity.collectionCount || 0)
        ),
        pagesWithDonations: calculateYoYChange(
          (entity.fundraiserWithDonationsCount || 0) + (entity.teamWithDonationsCount || 0) + (entity.collectionWithDonationsCount || 0),
          (previousYearEntity.fundraiserWithDonationsCount || 0) + (previousYearEntity.teamWithDonationsCount || 0) + (previousYearEntity.collectionWithDonationsCount || 0)
        ),
        fundraiserAverageRaisedAmount: calculateYoYChange(
          entity.fundraiserAverageRaisedAmount || 0,
          previousYearEntity.fundraiserAverageRaisedAmount || 0
        ),
        ambassadorTotalCount: calculateYoYChange(entity.ambassadorTotalCount || 0, previousYearEntity.ambassadorTotalCount || 0),
        ambassadorPageCount: calculateYoYChange(entity.ambassadorPageCount || 0, previousYearEntity.ambassadorPageCount || 0),
        ambassadorFundraisedAmount: calculateYoYChange(
          entity.ambassadorFundraisedAmount || 0,
          previousYearEntity.ambassadorFundraisedAmount || 0
        )
      };

      return {
        ...entity,
        previousYearEntity,
        yoyChanges
      };
    });

    // Sort entities by raised amount
    const sorted = sortEntitiesByRaised(entitiesWithYoY);
    processedGroups[year] = sorted;
  });

  return processedGroups;
};

/**
 * Get the field name for a metric based on online/offline toggle
 */
export const getMetricField = (metric: string, includeOffline: boolean): string => {
  const onlineFieldMap: { [key: string]: string } = {
    'raisedAmount': includeOffline ? 'raisedAmount' : 'onlineRaisedAmount', // calculated
    'donationCount': includeOffline ? 'donationCount' : 'donationOnlineCount',
    'donationAverageAmount': includeOffline ? 'donationAverageAmount' : 'donationAverageOnlineAmount',
    'saleRaisedAmount': includeOffline ? 'saleRaisedAmount' : 'saleRaisedOnlineAmount',
    'donationRaisedAmount': includeOffline ? 'donationRaisedAmount' : 'donationRaisedOnlineAmount',
    'ambassadorFundraisedAmount': includeOffline ? 'ambassadorFundraisedAmount' : 'ambassadorFundraisedOnlineAmount'
  };

  return onlineFieldMap[metric] || metric;
};

/**
 * Get the value of a metric from an entity (supports calculated fields like pageCount)
 */
export const getMetricValueForEntity = (
  entity: FranchiseEditionLinkedEntityModel,
  metric: string,
  includeOffline: boolean
): number => {
  // Handle calculated metrics
  if (metric === 'pageCount') {
    return (entity.fundraiserCount || 0) + (entity.teamCount || 0) + (entity.collectionCount || 0);
  }
  if (metric === 'pagesWithDonations') {
    return (entity.fundraiserWithDonationsCount || 0) + 
           (entity.teamWithDonationsCount || 0) + 
           (entity.collectionWithDonationsCount || 0);
  }
  if (metric === 'raisedAmount' && !includeOffline) {
    return (entity.saleRaisedOnlineAmount || 0) + (entity.donationRaisedOnlineAmount || 0);
  }

  const fieldName = getMetricField(metric, includeOffline);
  const value = (entity as any)[fieldName];
  return value !== null && value !== undefined ? Number(value) : 0;
};

/**
 * Check if sales row should be shown
 */
export const shouldShowSalesRow = (entity: FranchiseEditionLinkedEntityModel): boolean => {
  return (entity.saleCount || 0) > 0 || (entity.saleRaisedAmount || 0) > 0;
};

/**
 * Check if fundraising row should be shown
 */
export const shouldShowFundraisingRow = (entity: FranchiseEditionLinkedEntityModel): boolean => {
  const pageCount = (entity.fundraiserCount || 0) + (entity.teamCount || 0) + (entity.collectionCount || 0);
  return pageCount > 0;
};

/**
 * Check if PlaceHub row should be shown
 */
export const shouldShowPlaceHubRow = (entity: FranchiseEditionLinkedEntityModel): boolean => {
  return (entity.ambassadorTotalCount || 0) > 0;
};

/**
 * Calculate YoY change for any metric
 */
export const calculateYoYForMetric = (
  current: FranchiseEditionLinkedEntityModel,
  previous: FranchiseEditionLinkedEntityModel | undefined,
  metric: string,
  includeOffline: boolean
): number | undefined => {
  if (!previous) return undefined;
  
  const currentValue = getMetricValueForEntity(current, metric, includeOffline);
  const previousValue = getMetricValueForEntity(previous, metric, includeOffline);
  
  return calculateYoYChange(currentValue, previousValue);
};
